import { useAtom, useAtomValue, useSetAtom } from "jotai";
import TextArea from "../ui/TextArea";
import TextField from "../ui/TextField";
import { CustomerDetails, PaymentOption } from "./types";
import {
  listingGuidAtom,
  orderDataAtom,
  customerDetailsAtom,
  passengersAtom,
  orderStatusAtom,
  selectedDateAtom,
  selectedPackageAtom,
  selectedSeasonAtom,
  promoCodeAtom,
  campaignCodeAtom,
  campaignValidationAtom,
  campaignApplicationResultAtom,
  isOrderCreatingAtom,
  paymentOptionAtom,
  trackingParamsAtom,
  cateringEmailOptInAtom,
  boatingClubEmailOptInAtom,
} from "./store";
import { ccn, cn } from "../../styles/utils";
import { textStyles } from "../../styles/typography";
import {
  CreateMarketplaceBookingCommand,
  GetOrCreateCustomerByEmailCommand,
  Query,
  ValidateCampaignByEmailCommand,
  CreateBookingWorldPayCheckoutSessionCommand,
  CreateBookingPaymentIntentCommand,
  CreatePaymentIntentCommandResult,
  PaymentGateway,
  PricingMode,
  BookingTrackingParamsDto,
} from "@repo/rq-api-public";
import { TimeOnly } from "@repo/rq-api";
import { useState } from "react";
import Loading from "../ui/Loading";
import { handleApiErrors, extractErrorMessage } from "../../utils/apiErrors";
import { ApiValidationError } from "@repo/rq-api/customTypes";
import { captureEvent } from "@sentry/react";
import { ErrorIcon } from "../ui/icons";
import PhoneInput from "../ui/PhoneInput";
import { isPossiblePhoneNumber } from "react-phone-number-input";
import { getWidgetDomain } from "../../utils/url";
import {
  usePricingCalculateQuery,
  useListingQuery,
  useAvailabilityCheck,
} from "../../api/hooks";
import { useBookingStartDateTime } from "../hooks";

type FieldNames = "name" | "email" | "phone" | "message";
type ValidationError = ApiValidationError<FieldNames>;

export default function CustomerDetailsForm({
  handleNextStep,
}: {
  handleNextStep: () => void;
}) {
  const [isOrderCreateLoading, setIsOrderCreateLoading] =
    useAtom(isOrderCreatingAtom);

  const selectedDate = useAtomValue(selectedDateAtom);
  const listingGuid = useAtomValue(listingGuidAtom);
  const passengers = useAtomValue(passengersAtom);
  const selectedPackage = useAtomValue(selectedPackageAtom);
  const selectedSeason = useAtomValue(selectedSeasonAtom);
  const promoCode = useAtomValue(promoCodeAtom);
  const campaignCode = useAtomValue(campaignCodeAtom);
  const paymentOption = useAtomValue(paymentOptionAtom);
  const trackingParams = useAtomValue(trackingParamsAtom);

  const { data: listing } = useListingQuery();
  const isCalendarMode = listing?.pricingMode === PricingMode.Calendar;

  const [customerDetails, setCustomerDetails] = useAtom(customerDetailsAtom);
  const setOrderData = useSetAtom(orderDataAtom);
  const setOrderStatus = useSetAtom(orderStatusAtom);
  const campaignValidation = useAtomValue(campaignValidationAtom);
  const setCampaignValidation = useSetAtom(campaignValidationAtom);
  const setCampaignApplicationResult = useSetAtom(
    campaignApplicationResultAtom
  );

  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [failMessage, setFailMessage] = useState<string | null>(null);
  const [generalErrors, setGeneralErrors] = useState<unknown | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    [key in FieldNames]?: ValidationError;
  }>({});

  const startDateTime = useBookingStartDateTime();
  const { data: calculation } = usePricingCalculateQuery({
    promoCode: (!promoCode?.isLoading && promoCode?.submittedCode) || undefined,
    promoCodeStartDateTime: startDateTime,
  });

  const { data: paymentProvider } =
    Query.usePlatformAccountPaymentGatewayGETQuery();

  const cateringEmailOptIn = useAtomValue(cateringEmailOptInAtom);
  const boatingClubEmailOptIn = useAtomValue(boatingClubEmailOptInAtom);

  const { mutateAsync: createCustomer } = Query.useCustomersPOSTMutation();
  const { mutateAsync: createBooking } = Query.useBookingsPOSTMutation();
  const { mutateAsync: createWorldPayCheckoutSession } =
    Query.usePaymentsWorldpayCheckoutSessionPaymentPOSTMutation();

  const { mutateAsync: createPaymentIntent } =
    Query.usePaymentsCreatePaymentIntentPOSTMutation();
  const { mutateAsync: validateCampaignByEmail } =
    Query.usePricingsGuidCampaignValidationByCustomerEmailPOSTMutationWithParameters();

  const { checkAvailability } = useAvailabilityCheck();

  const clearError = (name: FieldNames) =>
    setFieldErrors((errors) => ({ ...errors, [name]: undefined }));

  const paymentAmountWithFee =
    (paymentOption === PaymentOption.Deposit
      ? calculation?.prePayment?.amountWithOnlineFee
      : calculation?.totalPrice) ?? 0;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Check field errors
    if (Object.values(fieldErrors).filter(Boolean).length > 0) {
      return;
    }

    setIsOrderCreateLoading(true);

    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData) as CustomerDetails;
    // use the phone number from the state (international format)
    data.phone = phoneNumber!;

    setCustomerDetails(data);

    if (isCalendarMode && !selectedSeason) {
      return;
    }
    if (!isCalendarMode && !selectedPackage) {
      return;
    }

    try {
      let customerGuid: string | undefined = undefined;
      let bookingGuid: string | undefined = undefined;

      // Create customer
      try {
        window.parent.postMessage(
          {
            ...data,
            trackEvent: "customerDetails",
          },
          "*"
        );

        const customerResult = await createCustomer(
          new GetOrCreateCustomerByEmailCommand({
            name: data.name,
            email: data.email,
            phone: data.phone,
          })
        );
        customerGuid = customerResult.customerGuid;
      } catch (error) {
        setFailMessage("Failed to create customer");
        handleApiErrors<FieldNames>(setGeneralErrors, (validationError) => {
          setFieldErrors((prev) => ({
            ...prev,
            [validationError.field as string]: validationError,
          }));
        })(error);

        captureEvent({
          message: "Failed to create customer",
          level: "info",
          extra: {
            order: {
              ...data,
              selectedDate,
              listingGuid,
              passengers,
              pricingGuid: selectedPackage?.pricingGuid ?? null,
              startTime: isCalendarMode ? null : selectedPackage?.startTime,
            },
            error,
          },
        });

        return;
      }

      if (!customerGuid) {
        throw new Error("Customer was not created");
      }

      // Validate campaign code with customer email if campaign code is present
      // Note: Campaign codes only work with pricing packages, not calendar mode
      let shouldUseCampaignCode = false;
      if (
        campaignCode &&
        campaignValidation.isValid === true &&
        selectedPackage?.pricingGuid
      ) {
        try {
          await validateCampaignByEmail({
            guid: selectedPackage!.pricingGuid,
            body: new ValidateCampaignByEmailCommand({
              campaignCode: campaignCode,
              customerEmail: data.email,
              startDate: selectedDate,
              startTime: selectedPackage!.startTime!,
            }),
          });
          // If validation succeeds, we can use the campaign code
          shouldUseCampaignCode = true;
        } catch (error) {
          // If validation fails, set campaign as invalid and continue without it
          setCampaignValidation({
            isValid: false,
            error: "Campaign not valid for this customer",
            isLoading: false,
          });
          setCampaignApplicationResult("failed");
          shouldUseCampaignCode = false;
        }
      }

      // Create booking
      try {
        const bookingResult = await createBooking(
          new CreateMarketplaceBookingCommand({
            name: data.name,
            email: data.email,
            phone: data.phone,
            customerGuid,
            date: selectedDate,
            listingGuid: listingGuid!,
            passengers,
            pricingGuid: isCalendarMode
              ? null
              : (selectedPackage!.pricingGuid ?? null),
            startTime: isCalendarMode
              ? new TimeOnly({ hour: 0, minute: 0 })
              : selectedPackage!.startTime!,
            notes: data.message,
            // Only use promo code if no valid campaign code is present
            promoCode: shouldUseCampaignCode
              ? undefined
              : promoCode?.submittedCode,
            // Only use campaign code if it's valid
            campaignCode: shouldUseCampaignCode ? campaignCode : undefined,
            widgetDomain: getWidgetDomain(),
            trackingParams: new BookingTrackingParamsDto(trackingParams),
            cateringEmailOptIn,
            boatingClubEmailOptIn,
          })
        );
        bookingGuid = bookingResult.bookingGuid;

        // If campaign code was used and booking was created successfully, mark it as success
        if (shouldUseCampaignCode) {
          setCampaignApplicationResult("success");
        }
      } catch (error) {
        // Check if this is a campaign-related error
        if (shouldUseCampaignCode) {
          // If the error is related to campaign (e.g., not first booking, not new customer)
          // we'll mark it as failed but allow the user to continue with normal booking
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          if (
            errorMessage.includes("first booking") ||
            errorMessage.includes("new customer") ||
            errorMessage.includes("campaign")
          ) {
            setCampaignApplicationResult("failed");
            // Continue without campaign code
            try {
              const retryResult = await createBooking(
                new CreateMarketplaceBookingCommand({
                  name: data.name,
                  email: data.email,
                  phone: data.phone,
                  customerGuid,
                  date: selectedDate,
                  listingGuid: listingGuid!,
                  passengers,
                  pricingGuid: isCalendarMode
                    ? null
                    : (selectedPackage!.pricingGuid ?? null),
                  startTime: isCalendarMode
                    ? new TimeOnly({ hour: 0, minute: 0 })
                    : selectedPackage!.startTime!,
                  notes: data.message,
                  promoCode: promoCode?.submittedCode,
                  campaignCode: undefined, // Don't use campaign code
                  widgetDomain: getWidgetDomain(),
                  trackingParams: new BookingTrackingParamsDto(trackingParams),
                  cateringEmailOptIn,
                  boatingClubEmailOptIn,
                })
              );
              bookingGuid = retryResult.bookingGuid;
            } catch (retryError) {
              setFailMessage("Failed to create booking");
              handleApiErrors(setGeneralErrors)(retryError);
              return;
            }
          } else {
            // Different error, handle normally
            setFailMessage("Failed to create booking");
            handleApiErrors(setGeneralErrors)(error);
            return;
          }
        } else {
          // No campaign code or normal error
          setFailMessage("Failed to create booking");
          handleApiErrors(setGeneralErrors)(error);
          return;
        }
      }

      // Check if the booking is available after creating the booking, so we have the data
      try {
        const availabilityResult = await checkAvailability({
          listingGuid: listingGuid!,
          selectedDate,
          selectedPackage,
        });

        if (!availabilityResult?.isAvailable) {
          setOrderStatus("SLOT_BOOKED");
          return;
        }
      } catch (error) {
        setFailMessage("Failed to check availability");
        handleApiErrors(setGeneralErrors)(error);

        captureEvent({
          message: "Failed to check availability",
          level: "info",
          extra: {
            order: {
              ...data,
              selectedDate,
              listingGuid,
              passengers,
              pricingGuid: selectedPackage?.pricingGuid ?? null,
              startTime: isCalendarMode ? null : selectedPackage?.startTime,
            },
            error,
          },
        });

        return;
      }

      if (!bookingGuid) {
        throw new Error("Booking was not created");
      }

      // Create payment intent/session based on payment provider
      if (paymentProvider?.paymentGateway !== PaymentGateway.AuthorizeNet) {
        if (paymentProvider?.paymentGateway === PaymentGateway.Stripe) {
          // Create Stripe PaymentIntent (original implementation)
          let paymentIntent: CreatePaymentIntentCommandResult;

          try {
            paymentIntent = await createPaymentIntent(
              new CreateBookingPaymentIntentCommand({
                bookingGuid,
                isPrePayment: paymentOption === PaymentOption.Deposit,
                expectedAmount: paymentAmountWithFee,
              })
            );
          } catch (error) {
            setFailMessage("Failed to create payment intent");
            handleApiErrors(setGeneralErrors)(error);

            captureEvent({
              message: "Failed to create payment intent",
              level: "info",
              extra: {
                order: {
                  ...data,
                  selectedDate,
                  listingGuid,
                  passengers,
                  pricingGuid: selectedPackage?.pricingGuid ?? null,
                  startTime: isCalendarMode ? null : selectedPackage?.startTime,
                },
                error,
              },
            });

            return;
          }

          if (!paymentIntent) {
            throw new Error("Payment intent was not created");
          }

          // Set order data with payment intent (original implementation)
          setOrderData({ ...paymentIntent, bookingGuid });
        }

        if (paymentProvider?.paymentGateway === PaymentGateway.WorldPay) {
          // Create WorldPay checkout session (default)
          let checkoutSession;

          try {
            checkoutSession = await createWorldPayCheckoutSession(
              new CreateBookingWorldPayCheckoutSessionCommand({
                bookingGuid,
                amount: paymentAmountWithFee,
              })
            );
          } catch (error) {
            setFailMessage("Failed to create checkout session");
            handleApiErrors(setGeneralErrors)(error);

            captureEvent({
              message: "Failed to create WorldPay checkout session",
              level: "info",
              extra: {
                order: {
                  ...data,
                  selectedDate,
                  listingGuid,
                  passengers,
                  pricingGuid: selectedPackage?.pricingGuid ?? null,
                  startTime: isCalendarMode ? null : selectedPackage?.startTime,
                },
                error,
              },
            });

            return;
          }

          if (!checkoutSession) {
            throw new Error("Checkout session was not created");
          }

          // Set order data with checkout session
          setOrderData({
            bookingGuid,
            sessionGuid: checkoutSession.sessionGuid,
            merchantId: checkoutSession.merchantId ?? undefined,
            amount: checkoutSession.amount,
            currency: checkoutSession.currency ?? undefined,
            checkoutSessionUrl: checkoutSession.checkoutSessionUrl ?? undefined,
          });
        }
      }

      // Go to next step with payment details
      setOrderData((prev) => ({
        ...prev,
        bookingGuid,
      }));
      handleNextStep();
    } catch (error) {
      const e = error as Error;
      setGeneralErrors(e?.message ?? error);

      captureEvent({
        message: e?.message ?? "Other error",
        level: "info",
        extra: {
          order: {
            ...data,
            selectedDate,
            listingGuid,
            passengers,
            pricingGuid: selectedPackage?.pricingGuid ?? null,
            startTime: isCalendarMode ? null : selectedPackage?.startTime,
          },
          error,
        },
      });
    } finally {
      setIsOrderCreateLoading(false);
    }
  };

  if (isOrderCreateLoading) {
    return (
      <div className="widget-contact-form">
        <h2
          className={ccn(
            textStyles.h2,
            "widget-contact-form__heading text-blue-dark60 md:mb-10 mb-4 text-center"
          )}
        >
          Add Your Contact Info
        </h2>
        <Loading />
      </div>
    );
  }

  return (
    <div className="widget-contact-form">
      <h2
        className={ccn(
          textStyles.h2,
          "widget-contact-form__heading text-blue-dark60 md:mb-10 mb-4 text-center"
        )}
      >
        Add Your Contact Info
      </h2>
      <form
        className="widget-contact-form__form flex flex-col gap-3"
        onSubmit={handleSubmit}
        id="contactInformation"
      >
        <TextField
          placeholder="First and Last Name"
          name="name"
          required
          defaultValue={customerDetails?.name}
          error={fieldErrors["name"]?.message}
          onChange={() => clearError("name")}
        />

        <TextField
          placeholder="E-mail Address"
          name="email"
          type="email"
          pattern="[A-Za-z0-9._+\-']+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}"
          required
          defaultValue={customerDetails?.email}
          error={fieldErrors["email"]?.message}
          onChange={() => clearError("email")}
        />

        <PhoneInput
          // name="phone"
          placeholder="Phone Number"
          defaultCountry="US"
          value={phoneNumber}
          onChange={(value) => {
            setPhoneNumber(value);

            if (value && !isPossiblePhoneNumber(value)) {
              setFieldErrors((prev) => ({
                ...prev,
                phone: {
                  field: "phone",
                  message: "Invalid phone number",
                },
              }));
            } else {
              setFieldErrors((prev) => ({
                ...prev,
                phone: undefined,
              }));
            }
          }}
          defaultValue={customerDetails?.phone}
          error={fieldErrors["phone"]?.message}
          required
        />

        <TextArea
          placeholder="Note or Special Request (optional)"
          name="message"
          rows={1}
          defaultValue={customerDetails?.message}
          error={fieldErrors["message"]?.message}
          onChange={() => clearError("message")}
        />
      </form>

      {generalErrors ? (
        <div className="widget-contact-form__error border rounded-lg px-5 py-4 text-red bg-redLight mt-3">
          <div className={cn(textStyles.body1, "flex gap-2 mb-2 font-medium")}>
            <ErrorIcon /> {failMessage && failMessage}
          </div>
          {campaignCode
            ? extractErrorMessage(generalErrors)
            : JSON.stringify(generalErrors)}
        </div>
      ) : null}
    </div>
  );
}
